/**
 * Phase 5: Event Tracking Utility
 * Tracks user interactions for the recommendation engine.
 * Respects cookie consent settings.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event types matching backend enum
export const EventType = {
  VIEW_CAMPAIGN: 'view_campaign',
  DONATE: 'donate',
  SHARE: 'share',
  SEARCH: 'search',
  CATEGORY_CLICK: 'category_click',
  HIDE_RECOMMENDATION: 'hide_recommendation'
};

// Get or create session ID
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('fth_session_id');
  if (!sessionId) {
    sessionId = 'ses_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('fth_session_id', sessionId);
  }
  return sessionId;
};

// Check if user has consented to analytics tracking
export const hasAnalyticsConsent = () => {
  try {
    const consent = JSON.parse(localStorage.getItem('cookie_consent') || '{}');
    return consent.analytics === true;
  } catch {
    return false;
  }
};

/**
 * Track a user event
 * @param {string} eventType - One of EventType values
 * @param {Object} options - Additional event data
 * @param {string} options.campaignId - Campaign ID if applicable
 * @param {string} options.category - Category if applicable
 * @param {Object} options.metadata - Additional metadata
 * @param {string} options.token - Auth token if logged in
 */
export const trackEvent = async (eventType, options = {}) => {
  // Check consent before tracking
  if (!hasAnalyticsConsent()) {
    console.debug('Event tracking skipped - no analytics consent');
    return { tracked: false, reason: 'no_consent' };
  }

  const sessionId = getSessionId();
  const { campaignId, category, metadata = {}, token } = options;

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/events/track`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        event_type: eventType,
        campaign_id: campaignId || null,
        category: category || null,
        metadata,
        analytics_consent: true
      })
    });

    if (!response.ok) {
      throw new Error(`Track event failed: ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Event tracking error:', error);
    return { tracked: false, error: error.message };
  }
};

// Convenience functions for common events

export const trackCampaignView = (campaignId, category, token = null) => {
  return trackEvent(EventType.VIEW_CAMPAIGN, { campaignId, category, token });
};

export const trackDonation = (campaignId, amount, token = null) => {
  return trackEvent(EventType.DONATE, { 
    campaignId, 
    metadata: { amount },
    token 
  });
};

export const trackShare = (campaignId, platform, token = null) => {
  return trackEvent(EventType.SHARE, { 
    campaignId, 
    metadata: { platform },
    token 
  });
};

export const trackSearch = (query, resultCount, token = null) => {
  return trackEvent(EventType.SEARCH, { 
    metadata: { query, result_count: resultCount },
    token 
  });
};

export const trackCategoryClick = (category, token = null) => {
  return trackEvent(EventType.CATEGORY_CLICK, { category, token });
};

export default {
  EventType,
  getSessionId,
  hasAnalyticsConsent,
  trackEvent,
  trackCampaignView,
  trackDonation,
  trackShare,
  trackSearch,
  trackCategoryClick
};
